import React, { useEffect, useState } from 'react';
import {
	Alert,
	Drawer,
	Form,
	Input,
	InputNumber,
	Modal,
	Select,
	Space,
} from 'antd';
import { Moment } from 'moment';
import {
	Currencies,
	ExpensesSubjectToHireDaysIntervals,
	ExpensesSubjectToHireDaysIntervalLabels,
	CalculationMethodForExpensesSubjectToHireDays,
	FixtureTypes,
} from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import { Values } from '@shared/utils/objectEnums';
import type { GetVoyageHireItemsResponse } from '@api/features/voyages/getVoyageHireItems';
import DatePicker from '@client/components/DatePicker';
import Button from '@client/components/Button';
import { updateHireItem } from '@client/lib/api';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import showErrorNotification from '@client/utils/showErrorNotification';

type Props = {
	editingItem: GetVoyageHireItemsResponse[number] | null;
	latestHirePeriodItemId: number | null;
	firstHirePeriodItemId: number | null;
	onClose: () => void;
	voyageId: number;
	fixtureCurrency: Values<typeof Currencies>;
	fixtureType: Values<typeof FixtureTypes> | undefined;
	onSuccess: () => void;
};

const EditHireItemDrawer = ({
	editingItem,
	latestHirePeriodItemId,
	firstHirePeriodItemId,
	onClose,
	voyageId,
	fixtureCurrency,
	fixtureType,
	onSuccess,
}: Props) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);

	const item = editingItem?.item as any;
	const itemType: string | undefined = item?.__itemType;
	const isTcOut = fixtureType === FixtureTypes.TC_OUT;
	const isLatestHirePeriod = itemType === 'hirePeriod' && item?.id === latestHirePeriodItemId;
	const isFirstHirePeriod = itemType === 'hirePeriod' && item?.id === firstHirePeriodItemId;
	const fromDisabled = isTcOut || !isFirstHirePeriod;
	const hasChildItems = itemType === 'hirePeriod' && (item?.children?.length ?? 0) > 0;
	const hasUninvoicedChildChanges = hasChildItems && (item?.children ?? []).some(
		(child: { isAmendment?: boolean; hireInvoiceId?: number | null }) => (
			child.isAmendment && child.hireInvoiceId == null
		),
	);
	const childChangesLostWarning = hasUninvoicedChildChanges ?
		'This hire period has uninvoiced changes to sub-items (e.g. commissions and CVE). Saving changes here will discard those edits.' :
		`This hire period has sub-items (e.g. commissions and CVE). 
		Any uninvoiced edits to those sub-items will be lost when you save changes here.`;

	useEffect(() => {
		form.resetFields();

		if (item == null) {
			return;
		}

		form.setFieldsValue({
			from: item.from,
			to: item.to,
			hireRate: item.hireRate,
			hireDescription: item.hireDescription,
			percentage: item.percentage,
			name: item.name,
			amount: item.amount,
			startDate: item.startDate,
			endDate: item.endDate,
			useUTC: item.useUTC,
			interval: item.interval,
			formulaType: item.formulaType,
			totalOverride: item.totalOverride,
		});
	}, [item, form]);

	const datesAreSame = (a: Moment | string | null | undefined, b: Moment | string | null | undefined) => {
		if (a == null && b == null) {
			return true;
		}

		if (a == null || b == null) {
			return false;
		}

		return toMoment(a).isSame(toMoment(b));
	};

	const getChangedAttributes = (values: Record<string, any>) => {
		const attributes: Record<string, any> = {};

		Object.keys(values).forEach((key) => {
			const next = values[key];
			const current = item?.[key];

			if (key === 'from' || key === 'to' || key === 'startDate' || key === 'endDate') {
				if (!datesAreSame(next, current)) {
					attributes[key] = next;
				}

				return;
			}

			if (next !== current) {
				attributes[key] = next;
			}
		});

		return attributes;
	};

	const saveHireItem = async (values: Record<string, any>) => {
		if (item == null) {
			return;
		}

		setLoading(true);

		try {
			const attributes = getChangedAttributes(values);
			const didHirePeriodDatesChange =
				itemType === 'hirePeriod' &&
				(attributes.from != null || attributes.to != null);

			await updateHireItem(voyageId, item.id, attributes);
			showSuccessNotification('Hire item updated');

			if (didHirePeriodDatesChange) {
				Modal.warning({
					content: [
						'This action has changed the dates of this hire period.',
						'Please make sure you invoice this change before you invoice the next hire period.',
						'Otherwise, hire periods may not be back-to-back',
					].join(' '),
					okText: 'OK',
				});
			}

			onSuccess();
			onClose();
		} catch (e) {
			showErrorNotification('Could not update hire item', e as Error);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = (values: Record<string, any>) => {
		if (item == null) {
			return;
		}

		if (hasChildItems) {
			Modal.confirm({
				title: 'Save hire period changes?',
				content: childChangesLostWarning,
				okText: 'Save anyway',
				cancelText: 'Cancel',
				onOk: () => saveHireItem(values),
			});

			return;
		}

		saveHireItem(values);
	};

	return (
		<Drawer
			open={editingItem != null}
			onClose={onClose}
			width={600}
			title={`Edit ${editingItem?.itemType ?? 'Hire Item'}`}
		>
			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
			>
				{hasChildItems && (
					<Alert
						type="warning"
						showIcon
						style={{ marginBottom: 16 }}
						message="Uninvoiced sub-item changes may be lost"
						description={childChangesLostWarning}
					/>
				)}
				{itemType === 'hirePeriod' && (
					<>
						<Form.Item
							name="from"
							label="From"
							help={isTcOut ?
								'Controlled by the TC-Out delivery on the port itinerary' :
								undefined}
						>
							<DatePicker
								time
								style={{ width: '100%' }}
								disabled={fromDisabled}
							/>
						</Form.Item>
						<Form.Item
							name="to"
							label="To"
						>
							<DatePicker
								time
								style={{ width: '100%' }}
								disabled={!isLatestHirePeriod}
							/>
						</Form.Item>
						<Form.Item
							name="hireRate"
							label="Hire rate"
						>
							<InputNumber
								style={{ width: '100%' }}
								precision={2}
								addonBefore={fixtureCurrency}
							/>
						</Form.Item>
						<Form.Item
							name="hireDescription"
							label="Description"
						>
							<Input />
						</Form.Item>
					</>
				)}
				{(itemType === 'addressCommission' || itemType === 'brokerCommission') && (
					<Form.Item
						name="percentage"
						label="Commission percentage"
					>
						<InputNumber
							style={{ width: '100%' }}
							precision={2}
							addonAfter="%"
						/>
					</Form.Item>
				)}

				{itemType === 'expenseSubjectToHireDays' && (
					<>
						<Form.Item
							name="amount"
							label="Amount"
						>
							<InputNumber
								style={{ width: '100%' }}
								precision={2}
								addonBefore={fixtureCurrency}
							/>
						</Form.Item>
						<Form.Item
							name="interval"
							label="Interval"
						>
							<Select>
								<Select.Option value={ExpensesSubjectToHireDaysIntervals.pcm}>
									{ExpensesSubjectToHireDaysIntervalLabels[ExpensesSubjectToHireDaysIntervals.pcm]}
								</Select.Option>
								<Select.Option value={ExpensesSubjectToHireDaysIntervals.perDay}>
									{ExpensesSubjectToHireDaysIntervalLabels[ExpensesSubjectToHireDaysIntervals.perDay]}
								</Select.Option>
							</Select>
						</Form.Item>
						<Form.Item
							name="formulaType"
							label="Formula"
						>
							<Select>
								<Select.Option value={CalculationMethodForExpensesSubjectToHireDays.ANNUALLY}>
									Annual
								</Select.Option>
								<Select.Option value={CalculationMethodForExpensesSubjectToHireDays.MONTHLY}>
									Monthly
								</Select.Option>
							</Select>
						</Form.Item>
					</>
				)}

				{itemType === 'revenueSubjectToDays' && (
					<>
						<Form.Item
							name="amount"
							label="Amount per day"
						>
							<InputNumber
								style={{ width: '100%' }}
								precision={3}
								addonBefore={fixtureCurrency}
							/>
						</Form.Item>
					</>
				)}
				<Form.Item>
					<Space>
						<Button
							type="primary"
							htmlType="submit"
							loading={loading}
						>
							Save
						</Button>
						<Button onClick={onClose}>
							Cancel
						</Button>
					</Space>
				</Form.Item>
			</Form>
		</Drawer>
	);
};

export default EditHireItemDrawer;
